import { useTranslation } from 'react-i18next';
import { Alert, Breakpoint, Typography } from 'app/component-library-wave';

import { ActiveCustomerLocationPage } from 'app/pages/page-wrapper';
import { FetchStatus } from 'app/store/root-types';
import { useContactDetails } from 'app/hooks/use-contact-details';

import { Card } from 'app/components/card/card';
import { ContainerFixed } from 'app/components/container';
import { Spinner } from 'app/components/spinner';
import { LogoType, PartnerLogo } from 'app/components/partner-logo/partner-logo';

import { EditButton } from './contact-details-edit-button';
import styles from './contact-details.module.scss';

export const ContactDetails: ActiveCustomerLocationPage = (props) => {
  const { userContext } = props;
  const { t } = useTranslation();

  const { contactDetails, fetchStatus, displayName, displayContactId } = useContactDetails(userContext);

  const showBillingAddressSection = contactDetails?.billingAddress || contactDetails?.syncBillingAddress;

  const renderAddress = (address: MinesiderBackend.CustomerAddress) => {
    if (address.postbox) {
      return address.postbox;
    }

    return (
      <>
        {address.street} {address.houseNr}
        {address.houseLetter}, {address.zip} {address.city}
      </>
    );
  };

  if (!contactDetails) {
    switch (fetchStatus) {
      case FetchStatus.NOT_STARTED:
      case FetchStatus.PENDING:
        return <Spinner />;
      default:
        return (
          <Alert
            alertType="warning"
            heading={t('pages.contactDetailsCommon.fetchingFailed')}
            headingElement="strong"
            isExpandable={false}
            role="alert"
          />
        );
    }
  }

  return (
    <div className={styles.container}>
      <ContainerFixed isNarrow={true}>
        <Typography variant="headline4" component="h1" maxBreakpoint={Breakpoint.TABLET} className={styles.cardTitle}>
          {t('pages.contactDetails.name')}
        </Typography>
        <Card className={styles.card}>
          <div className={styles.cardSection}>
            <Typography
              variant="uiText1"
              component="div"
              bold={true}
              maxBreakpoint={Breakpoint.TABLET}
              className={styles.label}
            >
              {t('pages.contactDetails.customerNumber')}
            </Typography>
            <Typography variant="uiText1" component="div" maxBreakpoint={Breakpoint.TABLET}>
              {contactDetails.customerId}
            </Typography>
          </div>
          <div className={styles.partnerSection}>
            <PartnerLogo className={styles.logo} logoType={LogoType.LOGO} />
            <span>
              <Typography
                variant="uiText1"
                component="div"
                bold={true}
                maxBreakpoint={Breakpoint.TABLET}
                className={styles.label}
              >
                {t('pages.contactDetails.partner')}
              </Typography>
              <Typography variant="uiText1" component="div" maxBreakpoint={Breakpoint.TABLET}>
                {userContext.partnerName}
              </Typography>
            </span>
          </div>
        </Card>

        <Typography variant="headline4" component="h2" maxBreakpoint={Breakpoint.TABLET} className={styles.cardTitle}>
          {t('pages.contactDetails.myContactInformation')}
        </Typography>
        <Card className={styles.card}>
          <div className={styles.cardSection}>
            <Typography
              variant="uiText1"
              component="div"
              bold={true}
              maxBreakpoint={Breakpoint.TABLET}
              className={styles.label}
            >
              {t('pages.contactDetailsCommon.customerRelationships')}
            </Typography>

            <Typography
              data-hj-suppress={true}
              variant="uiText1"
              component="div"
              maxBreakpoint={Breakpoint.TABLET}
              className={styles.name}
            >
              {displayName}
            </Typography>
            <Typography variant="uiText1" component="div" maxBreakpoint={Breakpoint.TABLET}>
              {contactDetails.isEnterprise
                ? t('pages.contactDetails.organizationNumber')
                : t('pages.contactDetails.born')}
              : {displayContactId}
            </Typography>
          </div>
          <div className={styles.cardSection}>
            <Typography
              variant="uiText1"
              component="div"
              bold={true}
              maxBreakpoint={Breakpoint.TABLET}
              className={styles.label}
            >
              {t('pages.contactDetailsCommon.contactDetails')}
            </Typography>
            <Typography
              variant="uiText1"
              component="div"
              maxBreakpoint={Breakpoint.TABLET}
              className={styles.emailAddress}
            >
              {contactDetails.emailAddress}
            </Typography>
            <Typography variant="uiText1" component="div" maxBreakpoint={Breakpoint.TABLET}>
              {contactDetails.mobileCountryCode} {contactDetails.mobileNumber}
            </Typography>
          </div>
          <div className={styles.cardSection}>
            <Typography
              variant="uiText1"
              component="div"
              bold={true}
              maxBreakpoint={Breakpoint.TABLET}
              className={styles.label}
            >
              {t('pages.contactDetailsCommon.contactAddress')}
            </Typography>
            <Typography data-hj-suppress={true} variant="uiText1" component="div" maxBreakpoint={Breakpoint.TABLET}>
              {contactDetails.contactAddress && renderAddress(contactDetails.contactAddress)}
            </Typography>
            {!showBillingAddressSection && <EditButton contactDetails={contactDetails} userContext={userContext} />}
          </div>
          {showBillingAddressSection && (
            <div>
              <Typography
                variant="uiText1"
                component="div"
                bold={true}
                maxBreakpoint={Breakpoint.TABLET}
                className={styles.label}
              >
                {t('pages.contactDetailsCommon.billingAddress')}
              </Typography>
              <Typography data-hj-suppress={true} variant="uiText1" component="div" maxBreakpoint={Breakpoint.TABLET}>
                {contactDetails.syncBillingAddress
                  ? t('pages.contactDetails.sameAsContactAddress')
                  : renderAddress(contactDetails.billingAddress!)}
              </Typography>
              <EditButton contactDetails={contactDetails} userContext={userContext} />
            </div>
          )}
        </Card>
      </ContainerFixed>
    </div>
  );
};
